import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AiOutlineClose, AiOutlineCloudUpload, AiOutlineFileText } from 'react-icons/ai';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { useSeriesService } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { ENV } from '../config/env';

/**
 * Construye el path completo de un folder caminando hacia la raíz por `parent`.
 * Post ADR-022: el dropdown lista TODOS los folders del árbol; mostrar solo el
 * `name` puede ser ambiguo (folders homónimos en distintas ramas).
 * Ej: para `FICAL → DEPAL → CNBA` devuelve `"CNBA / DEPAL / FICAL"`.
 */
const buildFolderPath = (folderId, folders) => {
  const byId = new Map(folders.map(f => [f.id, f]));
  const parts = [];
  let current = byId.get(folderId);
  let safety = 50;
  while (current && safety-- > 0) {
    parts.unshift(current.name);
    current = current.parent ? byId.get(current.parent) : null;
  }
  return parts.join(' / ');
};

/**
 * Modal multi-step para importar una definición de serie documental (FSD-006).
 *
 *   select → validating → (errors | confirm) → committing → success
 *
 * El header es un stepper de 3 círculos (Archivo · Validación · Confirmación).
 * Spec visual completo: docs/architecture/design/export-import-serie-ui-spec.md §2.
 */
const ImportSerieModal = ({ open, onClose, onImported, folders = [] }) => {
  const seriesService = useSeriesService();
  const notify = useNotification();

  const [step, setStep] = useState('select');
  const [file, setFile] = useState(null);
  const [fileClientError, setFileClientError] = useState(null);
  const [handle, setHandle] = useState(null);
  const [preview, setPreview] = useState(null);
  const [validationLevel, setValidationLevel] = useState(null); // 1|2|3 si falló
  const [validationErrors, setValidationErrors] = useState([]);

  const [idOverride, setIdOverride] = useState('');
  const [nameOverride, setNameOverride] = useState('');
  const [parentOverride, setParentOverride] = useState('');

  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const fileInputRef = useRef(null);

  const resetAll = useCallback(() => {
    setStep('select');
    setFile(null);
    setFileClientError(null);
    setHandle(null);
    setPreview(null);
    setValidationLevel(null);
    setValidationErrors([]);
    setIdOverride('');
    setNameOverride('');
    setParentOverride('');
    setCommitting(false);
    setResult(null);
  }, []);

  useEffect(() => {
    if (!open) resetAll();
  }, [open, resetAll]);

  // ESC cierra (siempre que no esté commiteando)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !committing) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, committing, onClose]);

  if (!open) return null;

  // ── File handling ──
  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    validateAndSetFile(f);
  };

  const validateAndSetFile = (f) => {
    setFileClientError(null);
    if (!f.name.toLowerCase().endsWith('.json')) {
      setFileClientError('El archivo debe ser .json');
      setFile(null);
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setFileClientError('El archivo supera 5 MB');
      setFile(null);
      return;
    }
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) validateAndSetFile(f);
  };

  // ── Actions ──
  const goValidate = async () => {
    if (!file) return;
    setStep('validating');
    try {
      const resp = await seriesService.validateImportSerie(file);
      // El backend devuelve ImportSessionPreview plano: handle + campos de preview al top-level.
      const data = resp.data || {};
      setHandle(data.handle);
      setPreview(data);
      // defaults del form
      setIdOverride(data.idOriginal || '');
      setNameOverride(data.nameOriginal || '');
      setParentOverride(data.parentExistsInDestination ? data.parentOriginal : '');
      setStep('confirm');
    } catch (err) {
      const payload = err?.response?.data;
      if (payload && typeof payload === 'object' && payload.level) {
        setValidationLevel(payload.level);
        setValidationErrors(payload.errors || []);
      } else {
        setValidationLevel(1);
        setValidationErrors([{ path: '$', reason: err?.message || 'Error de conexión' }]);
      }
      setStep('errors');
    }
  };

  const goCommit = async () => {
    if (!handle || !parentOverride) return;
    setCommitting(true);
    setStep('committing');
    try {
      const resp = await seriesService.commitImportSerie(handle, {
        idOverride: idOverride.trim(),
        nameOverride: nameOverride.trim(),
        parentOverride: parentOverride.trim(),
      });
      setResult(resp.data);
      setStep('success');
      if (onImported) onImported(resp.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'error desconocido';
      notify.error(`No se pudo importar la serie: ${detail}`);
      // volver a confirm para permitir corregir
      setStep('confirm');
    } finally {
      setCommitting(false);
    }
  };

  // ── Derivados para el header ──
  const stepperState = (key) => {
    const order = ['select', 'validating', 'errors', 'confirm', 'committing', 'success'];
    const currentIdx = order.indexOf(step);
    const keyIdx = { select: 0, validate: 1, confirm: 2 }[key];
    if (step === 'errors' && keyIdx === 1) return 'error';
    if (step === 'success' && keyIdx <= 2) return 'done';
    const curMappedIdx = {
      select: 0,
      validating: 1,
      errors: 1,
      confirm: 2,
      committing: 2,
      success: 2,
    }[step];
    if (keyIdx < curMappedIdx) return 'done';
    if (keyIdx === curMappedIdx) return 'active';
    return 'pending';
  };

  const title = {
    select: 'Importar serie documental',
    validating: 'Validando archivo',
    errors: 'Validación fallida',
    confirm: 'Confirmar importación',
    committing: 'Importando…',
    success: 'Importación completa',
  }[step];

  const canGoBack = step === 'errors' || (step === 'confirm' && !committing);

  // Id colisiona si el preview avisó y el usuario no lo cambió.
  const idCollides = !!preview?.idAlreadyExists
    && idOverride.trim().toUpperCase() === (preview?.idOriginal || '').toUpperCase();

  const canCommit =
    !committing &&
    !!parentOverride &&
    !!idOverride.trim() &&
    !!nameOverride.trim() &&
    !idCollides;

  const goBack = () => {
    if (step === 'errors') {
      resetAll();
      setStep('select');
    } else if (step === 'confirm') {
      // Regresar al selector de archivo; invalida handle (se regenera)
      setHandle(null);
      setPreview(null);
      setStep('select');
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !committing) onClose();
      }}
      style={styles.overlay}
    >
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerRow}>
            <h2 id="import-modal-title" style={styles.title}>
              {title}
            </h2>
            <button
              aria-label="Cerrar"
              onClick={onClose}
              disabled={committing}
              style={styles.closeBtn}
            >
              <AiOutlineClose size={18} />
            </button>
          </div>
          <Stepper stepperState={stepperState} />
        </div>

        {/* Body */}
        <div style={styles.body} key={step}>
          {step === 'select' && (
            <SelectView
              file={file}
              fileClientError={fileClientError}
              onPickClick={() => fileInputRef.current?.click()}
              onFileChange={handleFileChange}
              onDrop={handleDrop}
              fileInputRef={fileInputRef}
            />
          )}
          {step === 'validating' && <ValidatingView />}
          {step === 'errors' && (
            <ErrorsView
              level={validationLevel}
              errors={validationErrors}
              preview={preview}
            />
          )}
          {(step === 'confirm' || step === 'committing') && (
            <ConfirmView
              preview={preview}
              idOverride={idOverride}
              setIdOverride={setIdOverride}
              nameOverride={nameOverride}
              setNameOverride={setNameOverride}
              parentOverride={parentOverride}
              setParentOverride={setParentOverride}
              folders={folders}
              committing={committing}
            />
          )}
          {step === 'success' && <SuccessView result={result} />}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <div>
            {canGoBack && (
              <button onClick={goBack} style={styles.secondaryBtn}>
                ← Volver
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {step !== 'success' && (
              <button
                onClick={onClose}
                disabled={committing}
                style={styles.secondaryBtn}
              >
                Cerrar
              </button>
            )}
            {step === 'select' && (
              <button
                onClick={goValidate}
                disabled={!file}
                style={file ? styles.primaryBtn : styles.primaryBtnDisabled}
              >
                Siguiente
              </button>
            )}
            {step === 'errors' && (
              <button onClick={goBack} style={styles.primaryBtn}>
                Reintentar
              </button>
            )}
            {(step === 'confirm' || step === 'committing') && (
              <button
                onClick={goCommit}
                disabled={!canCommit}
                title={idCollides ? 'Cambiá el identificador antes de continuar' : undefined}
                style={canCommit ? styles.primaryBtn : styles.primaryBtnDisabled}
              >
                {committing ? 'Importando…' : 'Importar'}
              </button>
            )}
            {step === 'success' && (
              <button onClick={onClose} style={styles.primaryBtn}>
                Listo
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Sub-componentes ──

const Stepper = ({ stepperState }) => {
  const steps = [
    { key: 'select', label: 'Archivo' },
    { key: 'validate', label: 'Validación' },
    { key: 'confirm', label: 'Confirmación' },
  ];
  return (
    <div style={styles.stepper}>
      {steps.map((s, i) => {
        const state = stepperState(s.key);
        return (
          <React.Fragment key={s.key}>
            <StepCircle state={state} idx={i + 1} label={s.label} />
            {i < steps.length - 1 && (
              <div
                style={{
                  ...styles.stepperLine,
                  backgroundColor: state === 'done' ? '#00468B' : '#E5E7EB',
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const StepCircle = ({ state, idx, label }) => {
  const colors = {
    pending: { bg: '#F3F4F6', border: '#E5E7EB', fg: '#9CA3AF' },
    active: { bg: '#00468B', border: '#00468B', fg: '#fff' },
    done: { bg: '#E8F5E9', border: '#C8E6C9', fg: '#2E7D32' },
    error: { bg: '#FEF2F2', border: '#FECACA', fg: '#DC2626' },
  }[state];
  return (
    <div style={{ textAlign: 'center', minWidth: 80 }}>
      <div
        style={{
          ...styles.stepCircle,
          backgroundColor: colors.bg,
          borderColor: colors.border,
          color: colors.fg,
        }}
      >
        {state === 'done' ? <FaCheck size={11} /> : state === 'error' ? <FaTimes size={11} /> : idx}
      </div>
      <div style={styles.stepLabel}>{label}</div>
    </div>
  );
};

const SelectView = ({ file, fileClientError, onPickClick, onFileChange, onDrop, fileInputRef }) => (
  <div style={{ padding: 32 }}>
    <div
      role="button"
      tabIndex={0}
      onClick={onPickClick}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onPickClick()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      style={{
        cursor: 'pointer',
        border: `2px dashed ${file ? '#00468B' : '#D1D5DB'}`,
        borderRadius: 8,
        padding: '40px 24px',
        textAlign: 'center',
        backgroundColor: file ? '#F0F7FF' : '#FAFAFA',
        transition: 'all 0.2s ease',
      }}
    >
      {file ? (
        <AiOutlineFileText size={36} color="#00468B" />
      ) : (
        <AiOutlineCloudUpload size={36} color="#9CA3AF" />
      )}
      <div
        style={{
          marginTop: 12,
          fontSize: 15,
          fontWeight: 500,
          color: file ? '#00468B' : '#374151',
        }}
      >
        {file ? file.name : 'Seleccioná un archivo .json'}
      </div>
      <div style={{ marginTop: 6, fontSize: 12, color: '#6B7280' }}>
        {file
          ? `${(file.size / 1024).toFixed(1)} KB · listo para validar`
          : 'Arrastralo acá o hacé click para buscar en tu equipo'}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        style={{ display: 'none' }}
        onChange={onFileChange}
      />
    </div>
    {fileClientError && (
      <div style={styles.errorBox}>{fileClientError}</div>
    )}
    <p style={styles.selectFootnote}>
      Solo archivos JSON hasta 5 MB, generados desde otra instancia de {ENV.APP_NAME}.
    </p>
  </div>
);

const ValidatingView = () => (
  <div
    style={{
      padding: 64,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 16,
    }}
  >
    <div style={styles.spinner} aria-label="Cargando">
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
    <div style={{ fontSize: 14, color: '#374151' }}>
      Validando estructura y compatibilidad…
    </div>
  </div>
);

const LEVEL_LABELS = { 1: 'Estructura', 2: 'Versión', 3: 'Contenido' };

const humanMessageByLevel = (level, errors, preview) => {
  if (level === 1) {
    return `El archivo no parece un export válido de ${ENV.APP_NAME}. Asegurate de subir el JSON generado desde otra instancia.`;
  }
  if (level === 2) {
    const versionErr = (errors || [])[0]?.reason || '';
    return `Versión incompatible. ${versionErr}`;
  }
  if (level === 3) {
    const count = errors?.length ?? 0;
    return `El contenido del archivo tiene ${count} problema${count === 1 ? '' : 's'} que impiden importarlo. Revisalos en los detalles.`;
  }
  return 'No se pudo validar el archivo.';
};

const ErrorsView = ({ level, errors, preview }) => (
  <div style={{ padding: '24px 32px' }}>
    <div style={styles.levelStrip}>
      {[1, 2, 3].map((lvl) => {
        const state = lvl < level ? 'pass' : lvl === level ? 'fail' : 'pending';
        const c =
          state === 'pass'
            ? { bg: '#E8F5E9', fg: '#2E7D32' }
            : state === 'fail'
            ? { bg: '#FEF2F2', fg: '#DC2626' }
            : { bg: '#F3F4F6', fg: '#9CA3AF' };
        return (
          <div key={lvl} style={{ flex: 1, textAlign: 'center' }}>
            <div
              style={{
                ...styles.levelBadge,
                backgroundColor: c.bg,
                color: c.fg,
                border: `1.5px solid ${c.fg}`,
              }}
            >
              {state === 'pass' ? (
                <FaCheck size={12} />
              ) : state === 'fail' ? (
                <FaTimes size={12} />
              ) : (
                lvl
              )}
            </div>
            <div style={styles.stepLabel}>{LEVEL_LABELS[lvl]}</div>
          </div>
        );
      })}
    </div>

    <div style={styles.errorBoxLarge}>{humanMessageByLevel(level, errors, preview)}</div>

    <details style={{ marginTop: 16 }}>
      <summary style={styles.detailsSummary}>Ver detalles técnicos</summary>
      <div style={styles.detailsBody}>
        {(errors || []).map((e, i) => (
          <div key={i} style={{ marginBottom: 4 }}>
            <span style={{ color: '#6B7280' }}>{e.path}</span> — {e.reason}
          </div>
        ))}
      </div>
    </details>
  </div>
);

const Chip = ({ bg, fg, label }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 10,
      backgroundColor: bg,
      color: fg,
      fontSize: 11,
      fontWeight: 600,
    }}
  >
    {label}
  </span>
);

const ConfirmView = ({
  preview,
  idOverride,
  setIdOverride,
  nameOverride,
  setNameOverride,
  parentOverride,
  setParentOverride,
  folders,
  committing,
}) => {
  const flags = preview?.flags || {};
  // El id colisiona mientras el usuario no lo cambie respecto del original.
  const idCollides = !!preview?.idAlreadyExists
    && idOverride.trim().toUpperCase() === (preview?.idOriginal || '').toUpperCase();
  return (
    <div style={{ padding: '24px 32px' }}>
      <div style={styles.previewBox}>
        <div style={styles.previewTitle}>Archivo validado</div>
        <div style={styles.previewStats}>
          <span>
            <strong>{preview?.fieldCount ?? 0}</strong> campos
          </span>
          <span>
            <strong>{preview?.strategyCount ?? 0}</strong> estrategias
          </span>
          <span>
            Exportado desde v<strong>{preview?.bovedaVersion || '—'}</strong>
          </span>
        </div>
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {flags.ocrRequired && <Chip bg="#FFFBEB" fg="#D97706" label="OCR" />}
          {flags.reviewRequired && <Chip bg="#ECFDF5" fg="#059669" label="Revisión" />}
          {flags.approvalRequired && <Chip bg="#EFF6FF" fg="#2563EB" label="Aprobación" />}
          {flags.signatureRequired && <Chip bg="#FDF4FF" fg="#9333EA" label="Firma" />}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div>
          <label style={styles.label}>Identificador</label>
          <input
            value={idOverride}
            onChange={(e) => setIdOverride(e.target.value.toUpperCase())}
            style={{
              ...styles.input,
              borderColor: idCollides ? '#D97706' : idOverride ? '#00468B' : '#D1D5DB',
            }}
            placeholder={preview?.idOriginal || ''}
            disabled={committing}
            aria-label="Identificador de la serie"
            aria-invalid={idCollides ? 'true' : undefined}
          />
          {idCollides && (
            <p style={{ ...styles.helpHint, color: '#D97706' }}>
              Ya existe una serie con este identificador en esta instancia. Cambialo para continuar.
            </p>
          )}
        </div>
        <div>
          <label style={styles.label}>Nombre</label>
          <input
            value={nameOverride}
            onChange={(e) => setNameOverride(e.target.value)}
            style={styles.input}
            placeholder={preview?.nameOriginal || ''}
            disabled={committing}
            aria-label="Nombre de la serie"
          />
        </div>
      </div>

      <div>
        <label style={styles.label}>Folder padre</label>
        <select
          value={parentOverride}
          onChange={(e) => setParentOverride(e.target.value)}
          style={{
            ...styles.input,
            borderColor: parentOverride ? '#00468B' : '#D1D5DB',
          }}
          disabled={committing}
          aria-label="Folder padre"
        >
          <option value="">-- Seleccioná un folder --</option>
          {folders.map((f) => (
            <option key={f.id} value={f.id}>
              {`${buildFolderPath(f.id, folders)} (${f.id})`}
            </option>
          ))}
        </select>
        {preview?.parentExistsInDestination === false && (
          <p style={{ ...styles.helpHint, color: '#D97706' }}>
            El folder original "<strong>{preview?.parentOriginal}</strong>" no existe en esta
            instancia. Elegí uno disponible.
          </p>
        )}
      </div>
    </div>
  );
};

const SuccessView = ({ result }) => (
  <div style={{ padding: 48, textAlign: 'center' }}>
    <div
      style={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        backgroundColor: '#E8F5E9',
        color: '#2E7D32',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
      }}
    >
      <FaCheck size={24} />
    </div>
    <div
      style={{
        fontSize: 17,
        fontWeight: 600,
        color: '#111827',
        marginBottom: 6,
      }}
    >
      Serie importada correctamente
    </div>
    <div style={{ fontSize: 13, color: '#6B7280' }}>
      "{result?.name}" ya aparece en el listado.
    </div>
  </div>
);

// ── Estilos ──

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(17, 24, 39, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    animation: 'fadeIn 0.12s ease-out',
  },
  modal: {
    width: 560,
    maxWidth: '92vw',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    boxShadow: '0 20px 48px rgba(0, 70, 139, 0.18)',
    overflow: 'hidden',
  },
  header: {
    padding: '20px 24px 16px',
    borderBottom: '1px solid #F3F4F6',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 'bold',
    color: '#00468B',
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#9CA3AF',
    padding: 4,
    lineHeight: 0,
  },
  stepper: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 18,
    gap: 0,
  },
  stepperLine: {
    flex: 1,
    height: 2,
    marginTop: -18,
    transition: 'background-color 0.3s ease',
  },
  stepCircle: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: '1.5px solid',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: 12,
  },
  stepLabel: {
    marginTop: 6,
    fontSize: 11,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    animation: 'fadeIn 0.12s ease-out',
  },
  footer: {
    padding: '16px 24px',
    borderTop: '1px solid #F3F4F6',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#FAFAFA',
  },
  primaryBtn: {
    padding: '8px 16px',
    backgroundColor: '#00468B',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  primaryBtnDisabled: {
    padding: '8px 16px',
    backgroundColor: '#9CA3AF',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'not-allowed',
  },
  secondaryBtn: {
    padding: '8px 14px',
    backgroundColor: 'transparent',
    color: '#374151',
    border: '1px solid #D1D5DB',
    borderRadius: 4,
    fontSize: 13,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  errorBox: {
    marginTop: 12,
    padding: '10px 14px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    color: '#DC2626',
    fontSize: 13,
    borderRadius: 6,
  },
  errorBoxLarge: {
    padding: '14px 18px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 6,
    color: '#991B1B',
    fontSize: 14,
    lineHeight: 1.5,
  },
  selectFootnote: {
    marginTop: 16,
    fontSize: 12,
    color: '#9CA3AF',
    textAlign: 'center',
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid #E5E7EB',
    borderTopColor: '#00468B',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  levelStrip: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
    padding: '16px 20px',
    backgroundColor: '#FAFAFA',
    borderRadius: 8,
    border: '1px solid #F3F4F6',
  },
  levelBadge: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: 13,
  },
  detailsSummary: {
    cursor: 'pointer',
    fontSize: 12,
    color: '#6B7280',
    padding: '6px 0',
  },
  detailsBody: {
    marginTop: 8,
    padding: 12,
    backgroundColor: '#F9FAFB',
    border: '1px solid #F3F4F6',
    borderRadius: 4,
    fontFamily: 'Menlo, Consolas, monospace',
    fontSize: 12,
    color: '#374151',
    maxHeight: 200,
    overflowY: 'auto',
  },
  previewBox: {
    padding: '14px 18px',
    backgroundColor: '#F0F7FF',
    border: '1px solid #C7DBF0',
    borderRadius: 6,
    marginBottom: 20,
  },
  previewTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#00468B',
    marginBottom: 8,
  },
  previewStats: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    fontSize: 12,
    color: '#374151',
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 500,
    color: '#374151',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #D1D5DB',
    borderRadius: 4,
    fontSize: 13,
    transition: 'border-color 0.2s ease',
    boxSizing: 'border-box',
  },
  helpHint: {
    marginTop: 4,
    fontSize: 11,
    color: '#9CA3AF',
  },
};

export default ImportSerieModal;
